export class StatsTracker {
    pending = new Set();
    times = {};
    windowMs;
    pendingTimeoutMs;
    constructor(options) {
        const opts = typeof options === "number" ? { windowMs: options } : options;
        this.windowMs = opts.windowMs;
        this.pendingTimeoutMs = opts.pendingTimeoutMs ?? opts.windowMs * 5;
    }
    /** Tracks the execution time of a function (supports both sync and async) */
    track = (key, f) => {
        const { end } = this.start(key);
        try {
            const result = f();
            if (isPromise(result)) {
                return result.finally(end);
            }
            end();
            return result;
        }
        catch (err) {
            end();
            throw err;
        }
    };
    /**
     * Starts tracking time for a key and returns an object with an end() method.
     * Safe to call concurrently for the same key — each call gets its own timer.
     */
    start = (key) => {
        const entry = { key, startTime: now(), ended: false };
        this.pending.add(entry);
        return {
            end: () => {
                if (entry.ended)
                    return;
                entry.ended = true;
                this.pending.delete(entry);
                const t = now();
                (this.times[key] ??= []).push({ duration: t - entry.startTime, at: t });
            },
        };
    };
    /** Ends tracking for all currently active keys */
    endAll = () => {
        const t = now();
        for (const entry of this.pending) {
            if (entry.ended)
                continue;
            entry.ended = true;
            (this.times[entry.key] ??= []).push({ duration: t - entry.startTime, at: t });
        }
        this.pending.clear();
    };
    /** Returns performance statistics for all tracked keys within the window */
    stats = () => {
        this.pruneStalePending();
        const cutoff = now() - this.windowMs;
        const result = {};
        for (const key of Object.keys(this.times)) {
            const entries = this.times[key];
            if (!entries)
                continue;
            // Prune expired entries in place
            const live = entries.filter((x) => x.at > cutoff);
            this.times[key] = live;
            if (live.length === 0) {
                delete this.times[key];
                continue;
            }
            result[key] = computeStats(live);
        }
        return result;
    };
    /** Discards pending operations that have exceeded the timeout */
    pruneStalePending = () => {
        const cutoff = now() - this.pendingTimeoutMs;
        for (const entry of this.pending) {
            if (entry.startTime < cutoff) {
                entry.ended = true; // prevent late end() from recording
                this.pending.delete(entry);
            }
        }
    };
    /** Resets all tracked data */
    reset = () => {
        this.pending.clear();
        this.times = {};
    };
}
function computeStats(entries) {
    const n = entries.length;
    const fmt = (v) => Number(v.toFixed(2));
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    for (const e of entries) {
        sum += e.duration;
        if (e.duration < min)
            min = e.duration;
        if (e.duration > max)
            max = e.duration;
    }
    const mean = sum / n;
    let sqDiffSum = 0;
    for (const e of entries) {
        const diff = e.duration - mean;
        sqDiffSum += diff * diff;
    }
    const variance = sqDiffSum / n;
    return {
        count: n,
        mean: fmt(mean),
        min: fmt(min),
        max: fmt(max),
        variance: fmt(variance),
    };
}
const now = () => typeof performance !== "undefined" && "now" in performance ? performance.timeOrigin + performance.now() : Date.now();
const isPromise = (value) => !!value &&
    (typeof value === "object" || typeof value === "function") &&
    typeof value.then === "function";
//# sourceMappingURL=index.js.map